/**
 * reviews-lite — lazy REVIEWS.io review list.
 * Uses data-prl-sku (main identifier) and optional data-prl-skus (multi-variant).
 */
(() => {
  const API = 'https://api.reviews.io/reviews';

  function escapeHtml(value) {
    return String(value)
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;')
      .replace(/'/g, '&#039;');
  }

  function stars(rating) {
    const rounded = Math.max(0, Math.min(5, Math.round(Number(rating) || 0)));
    return '★★★★★'.slice(0, rounded) + '☆☆☆☆☆'.slice(0, 5 - rounded);
  }

  function toText(value) {
    if (typeof value === 'string') return value;
    if (typeof value === 'number') return String(value);
    if (value && typeof value === 'object') {
      return (
        toText(value.text)
        || toText(value.content)
        || toText(value.body)
        || toText(value.name)
        || ''
      );
    }
    return '';
  }

  function formatDate(date) {
    if (!date) return '';
    const parsed = new Date(date);
    if (Number.isNaN(parsed.getTime())) return date;
    return parsed.toLocaleDateString(undefined, { year: 'numeric', month: 'short', day: 'numeric' });
  }

  function normalizeReview(review) {
    return {
      author: toText((review.author && review.author.name) || review.reviewer || review.author_name) || 'Verified customer',
      rating: Number(review.rating || review.rating_value || 0),
      title: toText(review.title || ''),
      body: toText(review.comments || review.body || review.content || review.review || ''),
      date: toText(review.date_created || review.date || review.created_at || ''),
      verified: Number(review.verified || 0) === 1,
      timeAgo: toText(review.time_ago || '')
    };
  }

  function readAttr(node, name) {
    if (!node) return '';
    const value = node.getAttribute(name);
    return value == null ? '' : String(value).trim();
  }

  function primarySku(node) {
    return readAttr(node, 'data-prl-sku');
  }

  function allSkus(node) {
    return readAttr(node, 'data-prl-skus');
  }

  /** SKU sent to REVIEWS.io: single-SKU products use main identifier only. */
  function apiSku(node, preferPrimary) {
    const main = primarySku(node);
    const all = allSkus(node);
    const multi = node && node.getAttribute('data-prl-multi-variant') === 'true';

    if (preferPrimary || !multi) {
      return main || all;
    }
    return all || main;
  }

  function buildUrl(store, sku, page, perPage) {
    const url = new URL(API);
    url.searchParams.set('store', store);
    url.searchParams.set('type', 'product_review');
    url.searchParams.set('per_page', String(perPage));
    url.searchParams.set('page', String(page));
    if (sku) url.searchParams.set('sku', sku);
    return url.toString();
  }

  function reviewMarkup(review) {
    return `
      <li class="prl-review">
        <div class="prl-review__head">
          <div class="prl-review__identity">
            <span class="prl-review__author">${escapeHtml(review.author)}</span>
            ${review.verified ? '<div class="prl-review__verified"><span class="prl-review__icon">✔</span>Verified Customer</div>' : ''}
          </div>
        </div>
        <div class="prl-review__rating" aria-label="${review.rating} out of 5 stars">${stars(review.rating)}</div>
        ${review.title ? `<h3 class="prl-review__title">${escapeHtml(review.title)}</h3>` : ''}
        ${review.body ? `<p class="prl-review__body">${escapeHtml(review.body)}</p>` : ''}
        <div class="prl-review__footer">
          <span class="prl-review__date">${escapeHtml(review.timeAgo || formatDate(review.date))}</span>
        </div>
      </li>
    `;
  }

  function updateBlockSummary(root, average, count) {
    const summary = root.querySelector('[data-prl-summary]');
    if (!summary) return;
    const avg = Number(average || 0);
    const n = Number(count || 0);
    if (n === 0) return;

    summary.hidden = false;
    const avgEl = summary.querySelector('[data-prl-average]');
    const countEl = summary.querySelector('[data-prl-count]');
    const based = summary.querySelector('.prl-reviews__based');
    if (avgEl) {
      avgEl.hidden = false;
      avgEl.textContent = avg.toFixed(1);
    }
    if (countEl) countEl.textContent = String(n);
    if (based) based.innerHTML = `Based on <span data-prl-count>${n}</span> reviews`;
    const starsWrap = summary.querySelector('.prl-rating__stars');
    if (starsWrap) {
      starsWrap.style.setProperty('--rating-percent', `${Math.max(0, Math.min(100, (avg / 5) * 100))}%`);
    }
  }

  function showEmpty(root) {
    const state = root.querySelector('[data-prl-state]');
    const list = root.querySelector('[data-prl-list]');
    const moreBtn = root.querySelector('[data-prl-load-more]');
    const summary = root.querySelector('[data-prl-summary]');

    if (summary) {
      summary.hidden = false;
      const avgEl = summary.querySelector('[data-prl-average]');
      const starsWrap = summary.querySelector('.prl-rating__stars');
      const based = summary.querySelector('.prl-reviews__based');
      if (avgEl) avgEl.hidden = true;
      if (starsWrap) starsWrap.style.setProperty('--rating-percent', '0%');
      if (based) based.textContent = 'Not yet reviewed';
    }

    if (state) {
      state.hidden = false;
      state.textContent = 'Be the first to share your thoughts on this product.';
    }
    if (list) {
      list.hidden = true;
      list.innerHTML = '';
    }
    if (moreBtn) moreBtn.hidden = true;
  }

  async function fetchPage(store, sku, page, perPage) {
    const response = await fetch(buildUrl(store, sku, page, perPage), { headers: { Accept: 'application/json' } });
    if (!response.ok) throw new Error('fetch failed');
    const json = await response.json();
    return {
      json,
      reviews: Array.isArray(json.reviews) ? json.reviews : [],
      perPage
    };
  }

  async function loadReviews(root, sku) {
    const store = readAttr(root, 'data-prl-store-id');
    const perPage = Number(root.dataset.prlPerPage || 10);
    return fetchPage(store, sku, 1, perPage);
  }

  async function hydrateBlock(root) {
    const state = root.querySelector('[data-prl-state]');
    const list = root.querySelector('[data-prl-list]');
    const moreBtn = root.querySelector('[data-prl-load-more]');
    const store = readAttr(root, 'data-prl-store-id');
    let sku = apiSku(root, false);

    if (!store || !sku) {
      if (state) state.textContent = 'Reviews are unavailable for this product.';
      return;
    }

    try {
      let payload;
      try {
        payload = await loadReviews(root, sku);
      } catch (_multiErr) {
        const fallback = primarySku(root);
        if (fallback && fallback !== sku) {
          sku = fallback;
          payload = await loadReviews(root, sku);
        } else {
          throw _multiErr;
        }
      }

      const { json, reviews, perPage } = payload;
      const apiCount = Math.max(Number(json.review_count || 0), Number(json.results_count || 0));
      const apiAvg = Number(json.average_rating || 0);

      if (reviews.length === 0 && apiCount === 0) {
        showEmpty(root);
        return;
      }

      updateBlockSummary(root, apiAvg, apiCount || reviews.length);

      if (state) state.hidden = true;
      if (list) {
        list.hidden = false;
        list.innerHTML = reviews.map((r) => reviewMarkup(normalizeReview(r))).join('');
      }

      root._prlPage = 1;
      root._prlPerPage = perPage;
      root._prlSku = sku;
      root._prlHasMore = reviews.length >= perPage;

      if (moreBtn) {
        moreBtn.hidden = !root._prlHasMore;
        if (!moreBtn.dataset.prlBound) {
          moreBtn.dataset.prlBound = 'true';
          moreBtn.addEventListener('click', () => loadMore(root));
        }
      }
    } catch (_err) {
      if (state) {
        state.hidden = false;
        state.textContent = 'Reviews are temporarily unavailable.';
      }
    }
  }

  async function loadMore(root) {
    const list = root.querySelector('[data-prl-list]');
    const moreBtn = root.querySelector('[data-prl-load-more]');
    if (!list || !root._prlHasMore) return;

    const store = readAttr(root, 'data-prl-store-id');
    const sku = root._prlSku || apiSku(root, false);

    moreBtn.disabled = true;
    try {
      const nextPage = (root._prlPage || 1) + 1;
      const { reviews, perPage } = await fetchPage(store, sku, nextPage, root._prlPerPage || 10);
      if (reviews.length > 0) {
        list.insertAdjacentHTML('beforeend', reviews.map((r) => reviewMarkup(normalizeReview(r))).join(''));
      }
      root._prlPage = nextPage;
      root._prlHasMore = reviews.length >= perPage;
      moreBtn.hidden = !root._prlHasMore;
    } catch (_err) {
      /* keep existing list */
    }
    moreBtn.disabled = false;
  }

  function boot() {
    const blocks = document.querySelectorAll('[data-prl-reviews]');
    if (blocks.length === 0) return;

    if (!('IntersectionObserver' in window)) {
      blocks.forEach((block) => hydrateBlock(block));
      return;
    }

    const observer = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        if (!entry.isIntersecting) return;
        observer.unobserve(entry.target);
        hydrateBlock(entry.target);
      });
    }, { rootMargin: '200px 0px' });

    blocks.forEach((block) => observer.observe(block));
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot, { once: true });
  } else {
    boot();
  }
})();
